import { forwardRef, useState } from "react";
import styled from "styled-components";
import { Eye, EyeOff } from "lucide-react";
import colors from "@styling/colors";
import { borderRadius, spacing } from "@styling/sizing";
import { fontSizes } from "@styling/fonts";

export const StyledInput = styled.input<{ $hasToggle?: boolean }>`
  width: 100%;
  padding: ${spacing.x2}px ${spacing.x2}px;
  padding-right: ${({ $hasToggle }) =>
    $hasToggle ? `${spacing.x2 + 28}px` : `${spacing.x2}px`};
  border: 1px solid ${colors.border};
  border-radius: ${borderRadius.regular}px;
  font-size: ${fontSizes.regular}px;
  background-color: ${colors.background};
  color: ${colors.text};

  &:focus {
    outline: none;
    border-color: ${colors.primary};
  }

  &::placeholder {
    color: ${colors.textMuted};
  }

  &:disabled {
    background-color: ${colors.backgroundDark};
    color: ${colors.textMuted};
    cursor: not-allowed;
    opacity: 0.8;
  }
`;

const InputWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const ToggleButton = styled.button`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${spacing.x2 + 28}px;
  border: none;
  background: transparent;
  color: ${colors.textMuted};
  cursor: pointer;

  &:hover {
    color: ${colors.text};
  }

  &:focus-visible {
    outline: 2px solid ${colors.primary};
    outline-offset: 2px;
    color: ${colors.primary};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.8;
  }
`;

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  showPasswordToggle?: boolean;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  {
    showPasswordToggle = false,
    type,
    className,
    disabled,
    ...props
  },
  ref,
) {
  const [passwordVisible, setPasswordVisible] = useState(false);
  const hasPasswordToggle = showPasswordToggle && type === "password";

  if (!hasPasswordToggle) {
    return (
      <StyledInput ref={ref} type={type} className={className} disabled={disabled} {...props} />
    );
  }

  return (
    <InputWrapper className={className}>
      <StyledInput
        ref={ref}
        type={passwordVisible ? "text" : "password"}
        $hasToggle
        disabled={disabled}
        {...props}
      />
      <ToggleButton
        type="button"
        disabled={disabled}
        aria-label={passwordVisible ? "Hide" : "Show"}
        onClick={() => setPasswordVisible((visible) => !visible)}
      >
        {passwordVisible ? <EyeOff size={18} /> : <Eye size={18} />}
      </ToggleButton>
    </InputWrapper>
  );
});
